import React, { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Button } from "../components/duvi/Button";
import { Badge } from "../components/duvi/Badge";
import { Shield, X } from "lucide-react";
import { UserProfileImage } from "../components/duvi/UserProfileImage";
import { users } from "../api/client";
import { useQuery, useMutation } from "../hooks/useApi";
import { format } from "date-fns";
import { testIds } from "@duvi-agency/ui";

function formatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

function getInitials(name?: string): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function mapStatus(status: string): string {
  switch (status) {
    case "CONFIRMED":
    case "FORCE_CHANGE_PASSWORD":
      return "active";
    case "disabled":
    case "DISABLED":
      return "disabled";
    case "archived":
      return "archived";
    default:
      return status.toLowerCase();
  }
}

function getStatusBadgeVariant(status: string) {
  switch (status) {
    case "active":
      return "success";
    case "disabled":
    case "archived":
      return "secondary";
    default:
      return "default";
  }
}

export function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const {
    data: userData,
    isLoading,
    refetch,
  } = useQuery(() => users.get(userId!), [userId]);

  const forceResetMutation = useMutation(() =>
    users.forceResetPassword(userId!),
  );
  const disableMutation = useMutation(() => users.disable(userId!));
  const enableMutation = useMutation(() => users.enable(userId!));
  const archiveMutation = useMutation(() => users.archive(userId!));

  const handleForceReset = async () => {
    try {
      await forceResetMutation.mutate(undefined as never);
    } catch {
      // Error captured in mutation
    }
  };

  const handleDisable = async () => {
    try {
      await disableMutation.mutate(undefined as never);
      refetch();
    } catch {
      // Error captured in mutation
    }
  };

  const handleEnable = async () => {
    try {
      await enableMutation.mutate(undefined as never);
      refetch();
    } catch {
      // Error captured in mutation
    }
  };

  const handleArchive = async () => {
    try {
      await archiveMutation.mutate(undefined as never);
      setShowArchiveModal(false);
      refetch();
    } catch {
      // Error captured in mutation
    }
  };

  if (isLoading || !userData) {
    return (
      <AppShell
        breadcrumbs={["Platform", "Users", "Loading..."]}
        activeMenuItem="Users"
      >
        <div className="flex items-center justify-center py-16">
          <div
            className="text-duvi-text-secondary"
            style={{ fontSize: "var(--text-sm)" }}
          >
            Loading user details...
          </div>
        </div>
      </AppShell>
    );
  }

  const status = mapStatus(userData.status);
  const user = {
    id: userData.user_id,
    name: userData.name,
    email: userData.email,
    avatarUrl: userData.avatar_url,
    initials: getInitials(userData.name),
    status,
    createdAt: formatDate(userData.created_at),
    mfaEnabled: userData.mfa_enabled ?? false,
  };

  return (
    <AppShell
      breadcrumbs={["Platform", "Users", user.name]}
      activeMenuItem="Users"
    >
      <div
        data-testid={testIds.userDetailPage}
        className="grid grid-cols-[40%_60%] gap-6"
      >
        {/* Left Column - Profile Card */}
        <Card className="p-6 h-fit">
          <div className="flex flex-col items-center text-center">
            {/* Avatar */}
            <UserProfileImage
              userId={user.id}
              name={user.name}
              size={56}
              className="mb-4"
            />

            {/* Name */}
            <h1
              className="text-duvi-text-primary mb-2"
              style={{
                fontSize: "var(--text-xl)",
                fontWeight: "var(--font-weight-bold)",
              }}
            >
              {user.name}
            </h1>

            {/* Email */}
            <div
              className="text-duvi-text-secondary mb-3"
              style={{ fontSize: "var(--text-sm)" }}
            >
              {user.email}
            </div>

            {/* Status Badge */}
            <div className="mb-4">
              <Badge variant={getStatusBadgeVariant(user.status)}>
                {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
              </Badge>
            </div>

            {/* Metadata */}
            <div
              className="text-duvi-text-secondary space-y-1"
              style={{ fontSize: "var(--text-xs)" }}
            >
              <div>Created {user.createdAt}</div>
            </div>
          </div>
        </Card>

        {/* Right Column - Cards Stack */}
        <div className="space-y-6">
          {/* Security Card */}
          <Card className="p-6">
            <h2
              className="text-duvi-text-primary mb-4"
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              Security
            </h2>

            <div className="space-y-6">
              {/* MFA Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-duvi-success" />
                  <div>
                    <div
                      className="text-duvi-text-primary"
                      style={{
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-weight-bold)",
                      }}
                    >
                      Two-factor authentication
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {user.mfaEnabled ? (
                    <Badge variant="success">Enabled</Badge>
                  ) : (
                    <Badge variant="warning">Not set up</Badge>
                  )}
                </div>
              </div>

              {/* Passkeys Section */}
            </div>
          </Card>

          {/* Account Actions Card */}
          <Card className="p-6">
            <h2
              className="text-duvi-text-primary mb-4"
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              Account Actions
            </h2>

            <div className="space-y-3">
              <Button
                variant="secondary"
                className="w-full"
                onClick={handleForceReset}
                disabled={forceResetMutation.isLoading}
              >
                {forceResetMutation.isLoading
                  ? "Resetting..."
                  : "Force reset password"}
              </Button>

              {user.status === "disabled" ? (
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={handleEnable}
                  disabled={enableMutation.isLoading}
                >
                  {enableMutation.isLoading ? "Enabling..." : "Enable account"}
                </Button>
              ) : (
                <Button
                  variant="destructive-outline"
                  className="w-full"
                  onClick={handleDisable}
                  disabled={disableMutation.isLoading}
                >
                  {disableMutation.isLoading
                    ? "Disabling..."
                    : "Disable account"}
                </Button>
              )}

              <Button
                variant="destructive"
                className="w-full"
                onClick={() => setShowArchiveModal(true)}
              >
                Archive account
              </Button>
            </div>
          </Card>

          {/* Passkeys Section removed — login history to be implemented */}
        </div>
      </div>

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowArchiveModal(false)}
          />
          <div
            data-testid={testIds.archiveUserDialog}
            className="fixed inset-0 flex items-center justify-center z-50"
          >
            <Card className="w-full max-w-md p-6">
              <div className="flex items-start justify-between mb-4">
                <h2
                  className="text-duvi-text-primary"
                  style={{
                    fontSize: "var(--text-lg)",
                    fontWeight: "var(--font-weight-bold)",
                  }}
                >
                  Archive {user.name}?
                </h2>
                <button
                  onClick={() => setShowArchiveModal(false)}
                  className="text-duvi-text-secondary hover:text-duvi-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p
                className="text-duvi-text-secondary mb-6"
                style={{ fontSize: "var(--text-sm)" }}
              >
                This will disable their access permanently.
              </p>
              <div className="flex gap-3">
                <Button
                  data-testid={testIds.archiveUserDialogCancel}
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setShowArchiveModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  data-testid={testIds.archiveUserDialogConfirm}
                  variant="destructive"
                  className="flex-1"
                  onClick={handleArchive}
                  disabled={archiveMutation.isLoading}
                >
                  {archiveMutation.isLoading ? "Archiving..." : "Archive"}
                </Button>
              </div>
            </Card>
          </div>
        </>
      )}
    </AppShell>
  );
}
